import { createAxiosAgent } from 'src/agent';
import SessionService from 'src/services/SessionService';
import useUserStore from 'src/stores/user';

type EventName =
    | 'questionnaire_started'
    | 'questionnaire_validation_failed'
    | 'questionnaire_saved'
    | 'stream_analysis_started'
    | 'stream_analysis_result'
    | 'category_selected'
    | 'examples_viewed'
    | 'pricing_viewed'
    | 'offer_selected'
    | 'promo_result'
    | 'auto_renewal_toggled'
    | 'checkout_clicked'
    | 'checkout_failed'
    | 'checkout_created'
    | 'payment_redirected'
    | 'payment_returned';

type FunnelEvent = {
    event_id: string;
    attempt_id: string;
    event_name: EventName;
    occurred_at: string;
    properties: Record<string, string | number | boolean>;
};

type FunnelState = { attemptId: string; events: FunnelEvent[]; once: string[] };
const PREFIX = 'lm_product_funnel_v1_';
const MAX_EVENTS = 256;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const transport = createAxiosAgent({ timeout: 5000 });
let pendingFlush: Promise<void> | null = null;

const uuid = () =>
    typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
              const value = Math.floor(Math.random() * 16);
              return (character === 'x' ? value : (value & 3) | 8).toString(16);
          });
const accountId = () => useUserStore.getState().id;
const key = (id: number) => `${PREFIX}${id}`;
const enabled = () => Boolean(accountId() && SessionService.accessToken);

function read(id: number): FunnelState {
    try {
        const parsed = JSON.parse(localStorage.getItem(key(id)) || 'null');
        if (typeof parsed?.attemptId === 'string' && Array.isArray(parsed.events) && Array.isArray(parsed.once)) {
            return {
                attemptId: parsed.attemptId,
                events: parsed.events
                    .filter((event: FunnelEvent) => event && Date.now() - Date.parse(event.occurred_at) < MAX_AGE_MS)
                    .slice(-MAX_EVENTS),
                once: parsed.once.slice(-40),
            };
        }
    } catch {
        /* Damaged storage must not interrupt checkout. */
    }
    return { attemptId: uuid(), events: [], once: [] };
}

function write(id: number, state: FunnelState) {
    try {
        localStorage.setItem(key(id), JSON.stringify(state));
    } catch {
        /* Storage can be unavailable. */
    }
}

export function clearProductFunnel() {
    try {
        Object.keys(localStorage)
            .filter((item) => item.startsWith(PREFIX))
            .forEach((item) => localStorage.removeItem(item));
    } catch {
        /* Storage can be unavailable. */
    }
}

export function rotateProductFunnelAttempt() {
    const id = accountId();
    if (!id) return;
    const state = read(id);
    write(id, { attemptId: uuid(), events: state.events, once: [] });
}

export function recordProductFunnel(
    event_name: EventName,
    properties: FunnelEvent['properties'] = {},
    once?: string
): Promise<void> {
    if (!enabled()) return Promise.resolve();
    const id = accountId();
    const state = read(id);
    if (once && state.once.includes(once)) return Promise.resolve();
    const event: FunnelEvent = {
        event_id: uuid(),
        attempt_id: state.attemptId,
        event_name,
        occurred_at: new Date().toISOString(),
        properties,
    };
    state.events.push(event);
    state.events = state.events.slice(-MAX_EVENTS);
    if (once) state.once.push(once);
    write(id, state);
    return flushProductFunnel();
}

export function flushProductFunnel(): Promise<void> {
    if (!enabled()) return Promise.resolve();
    if (pendingFlush) return pendingFlush;
    const id = accountId();
    let blocked = false;
    pendingFlush = (async () => {
        let delivered = 0;
        while (delivered < MAX_EVENTS) {
            const event = read(id).events[0];
            if (!event) return;
            if (!enabled() || accountId() !== id) return;
            try {
                await transport.post('/acquisition/product-funnel/events/', event);
                const state = read(id);
                state.events = state.events.filter((item) => item.event_id !== event.event_id);
                write(id, state);
                delivered += 1;
            } catch (error: any) {
                const status = error?.response?.status;
                if (status && status !== 401 && status !== 409 && status !== 429 && status < 500) {
                    const state = read(id);
                    state.events = state.events.filter((item) => item.event_id !== event.event_id);
                    write(id, state);
                    delivered += 1;
                    continue;
                }
                blocked = true;
                return;
            }
        }
    })().finally(() => {
        pendingFlush = null;
        if (!blocked && enabled() && accountId() === id && read(id).events.length) {
            void flushProductFunnel();
        }
    });
    return pendingFlush;
}

if (typeof window !== 'undefined') {
    if (typeof useUserStore.subscribe === 'function') {
        useUserStore.subscribe((state, previous) => {
            if (previous.id && state.id !== previous.id) clearProductFunnel();
        });
    }
    window.addEventListener('online', () => {
        void flushProductFunnel();
    });
    window.addEventListener('focus', () => {
        void flushProductFunnel();
    });
}
